/**
 * Fuel Supply Term Benefit Calculator
 *
 * Independent/unbranded stations and stations with short-term fuel supply agreements
 * command a market premium due to operational flexibility and lower encumbrances.
 *
 * This modifier applies a graduated benefit based on remaining contract term:
 * - 0 years (independent/unbranded): 100% benefit
 * - 1 year remaining: 90% benefit
 * - 2 years: 80% benefit
 * - 3 years: 70% benefit
 * - 4 years: 20% benefit
 * - 5 years: 5% benefit
 * - 6+ years: No benefit
 *
 * Maximum benefit: +1.00x GPM, +2.00x EBITDA multiplier
 */

export interface FuelSupplyBenefit {
  qualifies: boolean;
  benefitPercentage: number;      // 0-100%
  gpmBoost: number;                // 0 to 1.00
  ebitdaMultiplierBoost: number;   // 0 to 2.00
  valuationAdjustment: number;     // Dollar adjustment to total value
  reason: string;
  originalValue: number;
  adjustedValue: number;
}

/**
 * Determines if a station qualifies for fuel supply benefit
 */
export function qualifiesForFuelSupplyBenefit(
  brandAffiliation: string,
  fuelSupplyYears: number
): boolean {
  // Check if independent/unbranded
  const isIndependent = isIndependentStation(brandAffiliation);

  // Qualifies if:
  // 1. Independent/unbranded with 0 years, OR
  // 2. Any station with 1-5 years remaining
  if (isIndependent && fuelSupplyYears === 0) {
    return true;
  }

  if (fuelSupplyYears >= 1 && fuelSupplyYears <= 5) {
    return true;
  }

  return false;
}

/**
 * Checks if a station is independent/unbranded
 */
function isIndependentStation(brandAffiliation: string): boolean {
  if (!brandAffiliation) return true;

  const normalized = brandAffiliation.toLowerCase().trim();

  const independentIndicators = [
    'independent',
    'unbranded',
    'no brand',
    'generic',
    'other'
  ];

  return independentIndicators.some(indicator =>
    normalized.includes(indicator)
  );
}

/**
 * Calculates the benefit percentage based on fuel supply years remaining
 */
function calculateBenefitPercentage(
  brandAffiliation: string,
  fuelSupplyYears: number
): { percentage: number; reason: string } {
  const isIndependent = isIndependentStation(brandAffiliation);

  // Independent/unbranded with no agreement = maximum benefit
  if (isIndependent && fuelSupplyYears === 0) {
    return {
      percentage: 100,
      reason: 'Independent/unbranded station with no fuel supply agreement - maximum flexibility premium'
    };
  }

  // Graduated benefit for short-term contracts
  switch (fuelSupplyYears) {
    case 1:
      return {
        percentage: 90,
        reason: '1 year remaining on fuel supply agreement - near-term flexibility premium'
      };
    case 2:
      return {
        percentage: 80,
        reason: '2 years remaining on fuel supply agreement - short-term flexibility premium'
      };
    case 3:
      return {
        percentage: 70,
        reason: '3 years remaining on fuel supply agreement - moderate flexibility premium'
      };
    case 4:
      return {
        percentage: 20,
        reason: '4 years remaining on fuel supply agreement - limited flexibility premium'
      };
    case 5:
      return {
        percentage: 5,
        reason: '5 years remaining on fuel supply agreement - minimal flexibility premium'
      };
    default:
      if (fuelSupplyYears >= 6) {
        return {
          percentage: 0,
          reason: `${fuelSupplyYears} years remaining - long-term agreement, no flexibility premium`
        };
      }
      return {
        percentage: 0,
        reason: 'Does not qualify for fuel supply benefit'
      };
  }
}

/**
 * Calculates the fuel supply benefit and adjusted valuation
 *
 * @param originalValue - The base property value from regression
 * @param grossProfit - Annual gross profit
 * @param noi - Net Operating Income (EBITDA equivalent)
 * @param brandAffiliation - Brand affiliation (independent, branded, etc.)
 * @param fuelSupplyYears - Years remaining on fuel supply agreement
 * @returns Fuel supply benefit details and adjusted valuation
 */
export function calculateFuelSupplyBenefit(
  originalValue: number,
  grossProfit: number,
  noi: number,
  brandAffiliation: string,
  fuelSupplyYears: number
): FuelSupplyBenefit {

  // Check if qualifies
  if (!qualifiesForFuelSupplyBenefit(brandAffiliation, fuelSupplyYears)) {
    return {
      qualifies: false,
      benefitPercentage: 0,
      gpmBoost: 0,
      ebitdaMultiplierBoost: 0,
      valuationAdjustment: 0,
      reason: fuelSupplyYears >= 6
        ? `${fuelSupplyYears} years remaining - long-term agreement, no benefit applies`
        : 'Does not qualify for fuel supply benefit',
      originalValue,
      adjustedValue: originalValue
    };
  }

  // Calculate benefit percentage
  const { percentage, reason } = calculateBenefitPercentage(brandAffiliation, fuelSupplyYears);

  // Calculate GPM boost (max +1.00x)
  const gpmBoost = (percentage / 100) * 1.00;
  const ebitdaMultiplierBoost = (percentage / 100) * 2.00;  // For display purposes

  // Calculate adjustment using GPM multiplier approach (like land value proportion adjustment)
  // 1. Calculate original GPM: originalValue / grossProfit
  // 2. Add fuel supply boost to GPM
  // 3. Recalculate value with adjusted GPM
  const originalGPM = grossProfit > 0 ? originalValue / grossProfit : 0;
  const adjustedGPM = originalGPM + gpmBoost;
  const adjustedValue = grossProfit * adjustedGPM;

  // Calculate the dollar adjustment for display
  const valuationAdjustment = adjustedValue - originalValue;

  return {
    qualifies: true,
    benefitPercentage: percentage,
    gpmBoost,
    ebitdaMultiplierBoost,
    valuationAdjustment,
    reason,
    originalValue,
    adjustedValue
  };
}

/**
 * Helper to format the benefit for display
 */
export function formatFuelSupplyBenefit(benefit: FuelSupplyBenefit): string {
  if (!benefit.qualifies) {
    return 'No fuel supply benefit';
  }

  const percentChange = ((benefit.adjustedValue - benefit.originalValue) / benefit.originalValue) * 100;

  return `Fuel supply benefit: +$${benefit.valuationAdjustment.toLocaleString()} (+${percentChange.toFixed(2)}%) - ${benefit.reason}`;
}
